import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, tokens, type Tokens } from '../api'
import { Button, Card, CardTitle, ConfirmButton, Field, Input, Note, ErrorText } from '../components/ui'

type Session = { id: string; user_agent: string; created_at: string; last_used_at: string }
export function AccountSecurity() {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<{ user: { name?: string; email: string } }>('/api/auth/me') })
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: () => api.get<{ sessions: Session[]; current_session_id: string }>('/api/auth/sessions') })
  const [name, setName] = useState<string | null>(null)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [message, setMessage] = useState('')
  const mutation = useMutation({
    mutationFn: (action: () => Promise<unknown>) => action(),
    onMutate: () => setMessage(''),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['me'] }); qc.invalidateQueries({ queryKey: ['sessions'] }); setMessage('Saved.') },
  })
  const signOut = () => { tokens.clear(); window.location.replace('/') }
  return <div className="space-y-5">
    {(mutation.error || me.error || sessions.error) && <ErrorText error={mutation.error || me.error || sessions.error} />}
    {message && <Note>{message}</Note>}
    <Card><CardTitle>Your account</CardTitle><form className="mt-4 space-y-4" onSubmit={event => { event.preventDefault(); mutation.mutate(() => api.patch('/api/auth/profile', { name: name ?? me.data?.user.name ?? '' })) }}>
      <p className="text-sm text-ink-faint">{me.data?.user.email}</p>
      <Field label="Name"><Input required maxLength={100} value={name ?? me.data?.user.name ?? ''} onChange={event => setName(event.target.value)} /></Field>
      <Button type="submit" disabled={mutation.isPending}>Save name</Button>
    </form></Card>
    <Card><CardTitle>Change password</CardTitle><form className="mt-4 space-y-4" onSubmit={event => {
      event.preventDefault(); mutation.mutate(async () => {
        const result = await api.post<{ tokens: Tokens }>('/api/auth/password', { current_password: current, new_password: next })
        tokens.set(result.tokens)
        setCurrent(''); setNext(''); setConfirm('')
      })
    }}>
      <Note>Changing your password signs out your other sessions.</Note>
      <Field label="Current password"><Input type="password" required value={current} onChange={event => setCurrent(event.target.value)} autoComplete="current-password" /></Field>
      <Field label="New password" help="At least 12 characters"><Input type="password" required minLength={12} value={next} onChange={event => setNext(event.target.value)} autoComplete="new-password" /></Field>
      <Field label="Confirm new password"><Input type="password" required value={confirm} onChange={event => setConfirm(event.target.value)} autoComplete="new-password" /></Field>
      <Button type="submit" disabled={mutation.isPending || !current || next.length < 12 || next !== confirm}>Change password</Button>
    </form></Card>
    <Card><CardTitle>Active sessions</CardTitle>
      {sessions.data?.sessions.map(session => <div key={session.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-line py-3">
        <div><p className="max-w-lg break-words text-sm">{session.user_agent || 'Unknown browser'} {session.id === sessions.data.current_session_id && '(This session)'}</p><p className="text-xs text-ink-faint">Last refreshed {new Date(session.last_used_at).toLocaleString()}</p></div>
        <ConfirmButton disabled={mutation.isPending} confirmLabel="Revoke session?" onConfirm={() => mutation.mutate(async () => {
          await api.del(`/api/auth/sessions/${session.id}`)
          if (session.id === sessions.data.current_session_id) signOut()
        })}>Revoke</ConfirmButton>
      </div>)}
      <div className="mt-4"><ConfirmButton disabled={mutation.isPending} confirmLabel="Sign out on every device?" onConfirm={() => mutation.mutate(async () => { await api.post('/api/auth/logout-all'); signOut() })}>Sign out everywhere</ConfirmButton></div>
    </Card>
  </div>
}
